import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
  buildPersonJsonLd,
  getPageSeo,
} from '../../../../config/seo';
import { personalInfo } from '../../../../config/personalInfo';

const stripStaticDuplicates = () => {
  const selectors = [
    'link[rel="canonical"]',
    'link[rel="alternate"][hreflang]',
    'meta[name="title"]',
    'meta[name="description"]',
    'meta[name="keywords"]',
    'meta[name="author"]',
    'meta[name="creator"]',
    'meta[name="publisher"]',
    'meta[name="language"]',
    'meta[name="robots"]',
    'meta[name="googlebot"]',
    'meta[name="bingbot"]',
    'meta[name="geo.region"]',
    'meta[name="geo.placename"]',
    'meta[name="format-detection"]',
    'meta[property^="og:"]',
    'meta[property^="profile:"]',
    'meta[name^="twitter:"]',
    'script[type="application/ld+json"]',
  ];

  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (!el.hasAttribute('data-rh')) {
        el.remove();
      }
    });
  });
};

export const MetaTags = () => {
  const { language } = useLanguage();
  const { pathname } = useLocation();
  const seo = getPageSeo(pathname, language);
  const canonical = absoluteUrl(seo.canonicalPath);
  const isHome = pathname === '/';
  const jsonLd = isHome ? buildPersonJsonLd(language) : null;
  const ogLocaleAlternate = language === 'en' ? 'es_ES' : 'en_US';

  useEffect(() => {
    stripStaticDuplicates();
  }, [language, pathname, seo.title, canonical]);

  return (
    <Helmet prioritizeSeoTags>
      <html lang={language} />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <meta name="author" content={`${personalInfo.fullName} (${SITE_NAME})`} />
      <meta name="creator" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="language" content={language} />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      <meta name="theme-color" content="#0D0D0D" />
      <meta name="msapplication-TileColor" content="#0D0D0D" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="geo.region" content="UY-MO" />
      <meta name="geo.placename" content="Montevideo" />

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}/`} />
      <link rel="alternate" hrefLang="es" href={`${SITE_URL}/?lang=es`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />

      <meta property="og:type" content={isHome ? 'profile' : 'website'} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:secure_url" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:alt" content={`${SITE_NAME} — ${personalInfo.fullName}`} />
      <meta property="og:site_name" content={`${SITE_NAME} Portfolio`} />
      <meta property="og:locale" content={seo.ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlternate} />
      {isHome && <meta property="profile:first_name" content={personalInfo.name} />}
      {isHome && <meta property="profile:username" content={SITE_NAME} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} — ${personalInfo.fullName}`} />
      <meta name="twitter:creator" content="@n33no" />
      <meta name="twitter:site" content="@n33no" />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};
